<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="1180px"
    :close-on-click-modal="false"
    title="配置规则"
  >
    <div v-if="ruleDetail" class="dialog-shell">
      <div class="dialog-meta">
        <span>平台组：{{ ruleDetail.groupName || '-' }}</span>
        <span>当前分类：{{ activeCategory ? activeCategory.categoryName : '-' }}</span>
        <span>当前状态：{{ ruleDetail.statusDesc || ruleDetail.status || '-' }}</span>
      </div>

      <div class="snapshot-shell">
        <nav class="snapshot-nav">
          <button
            v-for="(category, index) in categoryList"
            :key="category.categoryId"
            type="button"
            :class="{ active: activeCategoryId === category.categoryId }"
            @click="activeCategoryId = category.categoryId"
          >
            <span>{{ `${index + 1}. ${category.categoryName}` }}</span>
            <i :class="resolveCategoryStatus(category).statusClass">{{ resolveCategoryStatus(category).statusText }}</i>
          </button>
        </nav>

        <div v-if="activeCategory" class="snapshot-content">
          <div class="snapshot-content-head">
            <div>
              <h3>
                {{ activeCategory.categoryName }}
                <tool-tips :content="activeCategory.categoryDesc" />
              </h3>
              <p>编辑当前分类规则配置</p>
            </div>
            <span>{{ visibleFieldList.length }} 个配置项</span>
          </div>

          <div class="snapshot-field-grid">
            <div
              v-for="field in visibleFieldList"
              :key="field.fieldId"
              :class="['snapshot-field', { wide: field.controlType === 'MULTI_TEXT' }]"
            >
              <span class="snapshot-field-label">
                {{ field.fieldName }}
                <tool-tips :content="field.description" />
                <b v-if="isRequiredField(field)" class="required-badge">* 必填</b>
              </span>

              <el-select
                v-if="field.controlType === 'SINGLE_SELECT'"
                v-model="field.fieldValue"
                size="mini"
                clearable
                filterable
                @change="handleFieldValueChange(activeCategory)"
              >
                <el-option
                  v-for="item in field.options || []"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>

              <el-radio-group
                v-else-if="field.controlType === 'SWITCH'"
                v-model="field.fieldValue"
                @change="handleFieldValueChange(activeCategory)"
              >
                <el-radio
                  v-for="item in resolveSwitchOptions(field)"
                  :key="item.value"
                  :label="item.value"
                >
                  {{ item.label }}
                </el-radio>
              </el-radio-group>

              <el-checkbox-group
                v-else-if="field.controlType === 'CHECKBOX'"
                v-model="field.fieldValue"
                @change="handleFieldValueChange(activeCategory)"
              >
                <el-checkbox
                  v-for="item in field.options || []"
                  :key="item.code"
                  :label="item.code"
                >
                  {{ item.name }}
                </el-checkbox>
              </el-checkbox-group>

              <el-input-number
                v-else-if="field.controlType === 'NUMBER'"
                v-model="field.fieldValue"
                size="mini"
                :min="0"
                :controls="false"
                @change="handleFieldValueChange(activeCategory)"
              />

              <el-input
                v-else-if="field.controlType === 'MULTI_TEXT'"
                v-model="field.fieldValue"
                type="textarea"
                :rows="3"
                resize="none"
                @input="handleFieldValueChange(activeCategory)"
              />

              <el-input
                v-else
                v-model="field.fieldValue"
                size="mini"
                @input="handleFieldValueChange(activeCategory)"
              >
                <template v-if="hasUnitSelect(field)" slot="append">
                  <el-select v-model="field._selectedUnit" class="unit-select" size="mini">
                    <el-option
                      v-for="unit in getUnitOptions(field)"
                      :key="unit"
                      :label="unit"
                      :value="unit"
                    />
                  </el-select>
                </template>
                <template v-else-if="field.unit" slot="append">{{ field.unit }}</template>
              </el-input>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="handleConfirm">保存并发布</el-button>
    </div>
  </el-dialog>
</template>

<script>
import ToolTips from './ToolTips.vue'
import { isEmptyFieldValue, isFieldVisible, isRequiredField, serializeFieldValue, sortBySortOrder } from '../platformRuleUtils'

function clone (value) {
  return JSON.parse(JSON.stringify(value))
}

export default {
  name: 'RuleConfigDialog',
  components: {
    ToolTips
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    configRow: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      ruleDetail: null,
      activeCategoryId: ''
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      }
    },
    categoryList () {
      return sortBySortOrder((this.ruleDetail && this.ruleDetail.categories) || []).map((category) => ({
        ...category,
        fields: sortBySortOrder(category.fields || [])
      }))
    },
    activeCategory () {
      return this.categoryList.find(item => item.categoryId === this.activeCategoryId) || this.categoryList[0] || null
    },
    visibleFieldList () {
      if (!this.activeCategory) {
        return []
      }
      return this.getVisibleFields(this.activeCategory)
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (val) {
        if (val) {
          this.initDialogData()
          return
        }
        this.ruleDetail = null
        this.activeCategoryId = ''
      }
    }
  },
  methods: {
    async initDialogData () {
      if (!this.configRow) {
        return
      }
      const res = await this.$q.newProduct.platformImageRuleDetail({
        params: {
          groupId: this.configRow.groupId
        }
      })
      this.ruleDetail = clone(res.data || {})
      this.activeCategoryId = this.categoryList.length ? this.categoryList[0].categoryId : ''
      this.initFieldValueState()
      this.categoryList.forEach(category => this.clearHiddenFieldValue(category))
    },
    initFieldValueState () {
      this.categoryList.forEach((category) => {
        ;(category.fields || []).forEach((field) => {
          if (field.controlType === 'CHECKBOX' && !Array.isArray(field.fieldValue)) {
            const checkboxValue = field.fieldValue ? String(field.fieldValue).split(',').filter(Boolean) : []
            this.$set(field, 'fieldValue', checkboxValue)
          }
          if (this.hasUnitSelect(field)) {
            const unitOptions = this.getUnitOptions(field)
            const currentValue = field.fieldValue === null || field.fieldValue === undefined ? '' : String(field.fieldValue)
            const valueUnitMatch = currentValue.match(/^\s*(\d+(?:\.\d+)?)\s*(\D.*?)\s*$/)
            const unitText = valueUnitMatch ? valueUnitMatch[2].trim() : ''
            const matchedUnit = unitOptions.find(unit => unit.toLowerCase() === unitText.toLowerCase())
            this.$set(field, '_selectedUnit', matchedUnit || unitText || field._selectedUnit || unitOptions[0])
            if (valueUnitMatch) {
              this.$set(field, 'fieldValue', valueUnitMatch[1])
            }
          }
        })
      })
    },
    getVisibleFields (category) {
      const fields = (category && category.fields) || []
      return fields.filter(field => isFieldVisible(field, fields))
    },
    getUnitOptions (field) {
      return String((field && field.unit) || '').split(',').map(item => item.trim()).filter(Boolean)
    },
    hasUnitSelect (field) {
      return this.getUnitOptions(field).length > 1
    },
    handleFieldValueChange (category) {
      this.clearHiddenFieldValue(category)
    },
    clearHiddenFieldValue (category) {
      const fields = (category && category.fields) || []
      fields.forEach((field) => {
        if (!isFieldVisible(field, fields)) {
          field.fieldValue = null
        }
      })
    },
    isRequiredField (field) {
      return isRequiredField(field)
    },
    resolveCategoryStatus (category) {
      const fields = this.getVisibleFields(category)
      const requiredFields = fields.filter(item => isRequiredField(item))
      const completedRequiredFieldCount = requiredFields.filter(item => !isEmptyFieldValue(item)).length
      if (!requiredFields.length) {
        return {
          statusText: '待配置',
          statusClass: 'status-warn'
        }
      }
      if (completedRequiredFieldCount >= requiredFields.length) {
        return {
          statusText: '已完成',
          statusClass: 'status-ok'
        }
      }
      return {
        statusText: '缺必要项',
        statusClass: 'status-danger'
      }
    },
    getCategoryStatus (category) {
      const fields = this.getVisibleFields(category)
      const requiredFieldCount = fields.filter(item => isRequiredField(item)).length
      const completedFieldCount = fields.filter(item => !isEmptyFieldValue(item)).length
      let statusText = '待配置'
      let statusClass = 'status-warn'
      if (requiredFieldCount > 0 && completedFieldCount >= requiredFieldCount) {
        statusText = '已完成'
        statusClass = 'status-ok'
      } else if (completedFieldCount > 0) {
        statusText = '缺必要项'
        statusClass = 'status-danger'
      }
      return {
        statusText,
        statusClass
      }
    },
    resolveSwitchOptions (field) {
      if (field.options && field.options.length) {
        return field.options.map(item => ({
          label: item.name,
          value: item.code
        }))
      }
      return [
        { label: '是', value: 'Y' },
        { label: '否', value: 'N' }
      ]
    },
    validateConfig () {
      const affectedCategoryList = []
      this.categoryList.forEach((category) => {
        const fields = this.getVisibleFields(category)
        const hasInvalidField = fields.some(field => isRequiredField(field) && isEmptyFieldValue(field))
        if (hasInvalidField) {
          affectedCategoryList.push(category.categoryName)
        }
      })
      return affectedCategoryList
    },
    validateRatioFields () {
      const ratioReg = /^[1-9]\d*:[1-9]\d*$/
      const invalidField = this.categoryList.some((category) => {
        return this.getVisibleFields(category).some((field) => {
          const fieldValue = Array.isArray(field.fieldValue) ? field.fieldValue.join(',') : String(field.fieldValue === null || field.fieldValue === undefined ? '' : field.fieldValue).trim()
          return String(field.fieldName || '').includes('比例') && fieldValue && !ratioReg.test(fieldValue)
        })
      })
      if (invalidField) {
        this.$message.warning('格式错误，格式参考：a:b，ab需为正整数')
        return false
      }
      return true
    },
    buildPublishPayload () {
      return {
        groupId: this.ruleDetail.groupId,
        expectedRevision: this.ruleDetail.revision,
        changeDesc: '',
        categories: this.categoryList.map(category => ({
          categoryId: category.categoryId,
          fields: this.getVisibleFields(category)
            .map(field => ({
              fieldId: field.fieldId,
              fieldValue: serializeFieldValue(field)
            }))
        }))
      }
    },
    uppercasePayloadLetterValues (value) {
      if (Array.isArray(value)) {
        value.forEach(item => this.uppercasePayloadLetterValues(item))
        return value
      }
      if (value && typeof value === 'object') {
        Object.keys(value).forEach((key) => {
          value[key] = this.uppercasePayloadLetterValues(value[key])
        })
        return value
      }
      if (typeof value === 'string' && /[a-zA-Z]/.test(value)) {
        return value.toUpperCase()
      }
      return value
    },
    async handleConfirm () {
      const affectedCategoryList = this.validateConfig()
      if (affectedCategoryList.length) {
        this.$message.warning(`仍有未完成配置的分类：${affectedCategoryList.join('、')}`)
        return
      }
      if (!this.validateRatioFields()) {
        return
      }
      const publishPayload = this.uppercasePayloadLetterValues(this.buildPublishPayload())
      await this.$q.newProduct.platformImageRulePublish({
        data: publishPayload
      })
      this.$message.success('保存并发布成功')
      this.$emit('success')
      this.dialogVisible = false
    }
  }
}
</script>

<style lang="stylus" scoped>
.dialog-shell
  max-height 70vh
  overflow auto

.dialog-meta
  display flex
  flex-wrap wrap
  gap 16px
  margin-bottom 14px
  color #606266

.snapshot-shell
  display flex
  gap 14px

.snapshot-nav
  width 300px
  flex-shrink 0
  display flex
  flex-direction column
  gap 8px

.snapshot-nav button
  display flex
  justify-content space-between
  align-items center
  width 100%
  border 1px solid #ebeef5
  border-radius 6px
  background #fff
  padding 10px 12px
  text-align left
  cursor pointer

.snapshot-nav button.active
  border-color #409eff
  background #f4f8ff

.snapshot-nav i
  font-style normal
  flex-shrink 0
  margin-left 8px

.snapshot-content
  flex 1
  min-width 0
  border 1px solid #ebeef5
  border-radius 6px
  padding 14px
  background #fff

.snapshot-content-head
  display flex
  justify-content space-between
  gap 12px
  margin-bottom 14px

.snapshot-content-head h3
  margin 0 0 6px
  font-size 16px
  color #303133

.snapshot-content-head p
  margin 0
  color #909399
  font-size 12px

.snapshot-field-grid
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  gap 12px

.snapshot-field
  padding 12px
  border 1px solid #ebeef5
  border-radius 6px
  background #fafafa
  display flex
  flex-direction column
  gap 8px

.snapshot-field.wide
  grid-column 1 / -1

.snapshot-field-label
  color #606266
  font-weight 600
  display flex
  align-items center

.required-badge
  color #f56c6c
  margin-left 6px

.status-ok
  color #67c23a

.status-warn
  color #e6a23c

.status-danger
  color #f56c6c

>>> .el-select,
>>> .el-input-number,
>>> .el-textarea,
>>> .el-input
  width 100%

>>> .unit-select
  width 86px !important

@media (max-width: 1200px)
  .snapshot-shell
    flex-direction column
  .snapshot-nav
    width 100%
  .snapshot-field-grid
    grid-template-columns 1fr
</style>
